import { observable, computed, action } from 'mobx';
import Alert from 'react-s-alert';

import { analytics } from 'core/analytics';
import {
  Model,
  App,
  ArrayApp,
  AppRegistry,
  ModelRegistry,
  routerStore,
} from 'core/index';
import { inWhitelabel } from 'core/whitelabel';

import { FormStore } from 'libs/forms/app';

import { PermissionedModel } from 'apps/permissions/app';
import { FileModel, FileApp } from 'apps/file/app';
import { modalStore } from 'apps/modals/app';
import { EditManufaturerInfoForm } from 'apps/manufacturing/services/views/editInfoForm';

import { conf } from 'apps/manufacturing/signup/config.yaml';
import { conf as industriesConf } from 'apps/manufacturing/services/industries.yaml';
import { conf as certificatesConf } from 'apps/manufacturing/services/certificates.yaml';
import { conf as globalConf } from 'apps/config.yaml';

/* THIS APP IS LARGELY UNUSED EXCEPT FOR A FEW PARTs e.g ManufacturerModel is used (but not most of it's properties) 
TODO: Remove most of this module and replace it with a new ManufacturerModel */

const { imageDefaults } = globalConf;

export class FactoryModel extends PermissionedModel {
  static registryName = 'FactoryModel';
  @observable address = '';
  @observable geolocation = {};
}

export class FactoryApp extends App {
  static registryName = 'FactoryApp';
  static Model = FactoryModel;
}

export class FactoryArrayApp extends ArrayApp {
  static registryName = 'FactoryArrayApp';
  static ElementApp = FactoryApp;
}

export class MaterialModel extends Model {
  static registryName = 'MaterialModel';
  @observable name = '';
  @observable parentSubcategory = null;
  @observable parentCategory = null;
}

export class MaterialApp extends App {
  static registryName = 'MaterialApp';
  static Model = MaterialModel;
}

export class MaterialArrayApp extends ArrayApp {
  static registryName = 'MaterialArrayApp';
  static ElementApp = MaterialApp;
}

export class MaterialSubcategoryModel extends Model {
  static registryName = 'MaterialSubcategoryModel';
  static ModelProps = {
    materials: [MaterialModel],
  };

  @observable name = '';
  @observable materials = [];
  @observable parentCategory = null;
}

export class MaterialSubcategoryApp extends App {
  static registryName = 'MaterialSubcategoryApp';
  static AppProps = {
    materials: MaterialArrayApp,
  };

  static Model = MaterialSubcategoryModel;
}

export class MaterialSubcategoryArrayApp extends ArrayApp {
  static registryName = 'MaterialSubcategoryArrayApp';
  static ElementApp = MaterialSubcategoryApp;
}

export class MaterialCategoryModel extends Model {
  static registryName = 'MaterialCategoryModel';
  static ModelProps = {
    subcategories: [MaterialSubcategoryModel],
  };

  @observable name = '';
  @observable subcategories = [];
}

export class MaterialCategoryApp extends App {
  static registryName = 'MaterialCategoryApp';
  static AppProps = {
    subcategories: MaterialSubcategoryArrayApp,
  };

  static Model = MaterialCategoryModel;
}

export class MaterialCategoryArrayApp extends ArrayApp {
  static registryName = 'MaterialCategoryArrayApp';
  static ElementApp = MaterialCategoryApp;
}

export class ManufacturingProcessModel extends Model {
  static registryName = 'ManufacturingProcessModel';
  static ModelProps = {
    usableMaterials: [MaterialModel],
  };

  @observable name = '';
  @observable parentCategory = null;
  @observable usableMaterials = [];
}

export class ManufacturingProcessApp extends App {
  static registryName = 'ManufacturingProcessApp';
  static AppProps = {
    usableMaterials: MaterialArrayApp,
  };

  static Model = ManufacturingProcessModel;
}

export class ManufacturingProcessArrayApp extends ArrayApp {
  static registryName = 'ManufacturingProcessArrayApp';
  static ElementApp = ManufacturingProcessApp;
}

export class ManufacturingProcessCategoryModel extends Model {
  static registryName = 'ManufacturingProcessCategoryModel';
  static ModelProps = {
    processes: [ManufacturingProcessModel],
  };

  @observable name = '';
  @observable processes = [];
}

export class ManufacturingProcessCategoryApp extends App {
  static registryName = 'ManufacturingProcessCategoryApp';
  static AppProps = {
    processes: ManufacturingProcessArrayApp,
  };

  static Model = ManufacturingProcessCategoryModel;
}

export class ManufacturingProcessCategoryArrayApp extends ArrayApp {
  static registryName = 'ManufacturingProcessCategoryArrayApp';
  static ElementApp = ManufacturingProcessCategoryApp;
}

export class ServiceModel extends Model {
  static registryName = 'ServiceModel';
  static ModelProps = {
    process: ManufacturingProcessModel,
    materials: [MaterialModel],
  };

  @observable description = '';
  @observable process = null;
  @observable materials = [];
}

export class ServiceApp extends App {
  static registryName = 'ServiceApp';
  static AppProps = {
    process: ManufacturingProcessApp,
    materials: MaterialArrayApp,
  };

  @computed get selected() {
    const selected = {};
    this.materials.forEach(material => {
      selected[material.name] = true;
    });
    return selected;
  }

  @computed get materialCategories() {
    const categories = {};
    this.materials.forEach(material => {
      // Default case - material is a category itself
      let parentName = material.name;
      const { name } = material;
      if (material.parentSubcategory != null) {
        const {
          parentSubcategory: { parentCategory },
        } = material;
        parentName = parentCategory.name;
      } else if (material.parentCategory != null) {
        const { parentCategory } = material;
        parentName = parentCategory.name;
      }
      if (!categories[parentName]) {
        categories[parentName] = [];
      }
      categories[parentName].push({ name });
    });
    return categories;
  }

  @action Select = material => {
    const foundMaterial = this.materials.find(m => m.name === material.name);
    if (foundMaterial) {
      this.materials.remove(foundMaterial);
    } else {
      this.materials.push(material);
    }
  };

  static Model = ServiceModel;
}

export class ServiceArrayApp extends ArrayApp {
  static registryName = 'ServiceArrayApp';
  static ElementApp = ServiceApp;

  @computed get processCategoryNames() {
    return Object.keys(this.table);
  }

  @computed get table() {
    const table = {};
    this.apps.forEach(service => {
      const { process, materialCategories } = service;

      const { parentCategory, name } = process;
      const parentName = parentCategory ? parentCategory.name : name;
      if (!table[parentName]) {
        table[parentName] = [];
      }
      table[parentName].push({
        name,
        category: parentCategory,
        service,
        materialCategoriesTable: materialCategories,
      });
    });

    return table;
  }

  @computed get selected() {
    const selected = {};
    this.forEach(service => {
      selected[service.process.name] = true;
    });
    return selected;
  }

  @action Select = process => {
    const foundService = this.find(
      service => service.process.name === process.name,
    );
    if (foundService) {
      this.remove(foundService);
    } else {
      const newService = new ServiceApp(this, { process, materials: [] });
      this.push(newService);
    }
  };
}

export class ManufacturerModel extends PermissionedModel {
  static registryName = 'ManufacturerModel';
  static ModelProps = {
    factories: [FactoryModel],
    avatar: FileModel,
    initiative: 'InitiativeModel',
    services: [ServiceModel],
  };

  @observable companyName = '';
  @observable enquiryEmail = '';
  @observable telephone = '';
  @observable address = '';
  @observable geolocation = {};
  @observable foundingYear = '';
  @observable shipsTo = '';
  @observable numEmployees = '';
  @observable numMachines = '';
  @observable companyType = '';
  // FIXME add to conf, model, etc
  @observable leadTime = '';
  @observable minimumOrderQuantity = '';
  @observable fileFormats;
  @observable industries;
  @observable certificates;

  @observable initiative = null;
  @observable avatar;

  @observable factories = [];
  @observable services = [];

  @observable caseStudies = [];
  @observable certificates = [];
}
ModelRegistry.add(ManufacturerModel);

export class ManufacturerApp extends App {
  static registryName = 'ManufacturerApp';
  static AppProps = {
    factories: FactoryArrayApp,
    avatar: FileApp,
    initiative: 'InitiativeApp',
    services: ServiceArrayApp,
  };

  static Model = ManufacturerModel;

  @computed get servicesTabs() {
    return Object.entries(this.services.table).map(
      ([manufacturingProcessCategory, mpData]) => {
        const processes = mpData.map(manufacturingProcess => {
          const materialCategories = Object.entries(
            manufacturingProcess.materialCategoriesTable,
          ).map(([materialCategory, mData]) => {
            return {
              name: materialCategory,
              materials: mData,
            };
          });
          return {
            name: manufacturingProcess.name,
            materialCategories,
          };
        });
        return {
          name: manufacturingProcessCategory,
          processes,
        };
      },
    );
  }

  @computed get title() {
    return this.initiative ? this.initiative.title : '';
  }

  @computed get initiativeSlug() {
    return this.initiative ? this.initiative.slug : '';
  }

  @computed get inSpace() {
    // Required for File upload
    if (this.parent.parent && this.parent.parent.initiative) {
      return this.parent.parent.initiative.inSpace;
    }
    return {};
  }

  @computed get imageFallbackPath() {
    if (inWhitelabel()) {
      return 'assets/avatars/fallback/project-grey.png';
    }
    return imageDefaults.project;
  }

  @computed get location() {
    if (!this.geolocation) return null;
    return [this.geolocation.longitude, this.geolocation.latitude];
  }

  @computed get route() {
    if (!this.initiative) return {};
    return {
      name: 'initiative.services',
      params: {
        initiative: this.initiative.slug,
      },
    };
  }

  @observable editingAvatar = false;
  @observable editingInfo = false;

  @action SetEditingAvatar = bool => {
    this.editingAvatar = bool;
    if (bool) {
      this.editManufacturerAvatarForm = new FormStore(
        this,
        conf,
        'editManufacturerAvatar',
      );
    }
  };

  @action SetEditingInfo = bool => {
    this.editingInfo = bool;
    if (bool) {
      this.editManufacturerInfoForm = new FormStore(
        this,
        conf,
        'editManufacturerInfo',
      );
      modalStore.SetModalComponent(EditManufaturerInfoForm, { manager: this });
    } else {
      modalStore.Close();
    }
  };

  @action
  SaveManufacturer = () => {
    // Saves info section
    if (!this.editManufacturerInfoForm.isValid) {
      this.editManufacturerInfoForm.alert();
    } else {
      this.editManufacturerInfoForm.SetPosting(true);
      this.manager.SubmitManufacturer();
    }
  };

  @observable editingIndustries = false;
  @observable savingIndustries = false;
  @observable selectableIndustries = industriesConf.industries;

  @action SaveManufacturerIndustries = () => {
    this.industries = [];
    Object.keys(this.selectableIndustries).forEach(industry => {
      if (this.selectableIndustries[industry].selected) {
        this.industries.push(industry);
      }
    });
    this.SetSavingIndustries(true);
    this.manager.SubmitIndustries();
  };

  @action SetEditingIndustries = bool => {
    this.editingIndustries = bool;
    if (bool) {
      if (this.industries) {
        this.industries.forEach(industry => {
          this.selectableIndustries[industry].selected = true;
        });
      }
    } else {
      this.SetSavingIndustries(false);
    }
  };

  @action SetSavingIndustries = bool => {
    this.savingIndustries = bool;
  };

  @action SetIndustrySelected = (industry, bool) => {
    this.selectableIndustries[industry].selected = bool;
  };

  @observable editingCertificates = false;
  @observable selectableCertificates = certificatesConf.certificates;

  @action SaveInfo = () => {
    // Generic mutation call for both editingAvatar and editingInfo
    const avatarField = this.editManufacturerAvatarForm.fieldStores.get(
      'avatar',
    );
    if (avatarField && avatarField.fileModified) {
      avatarField.getUpdate(this.manager.SubmitManufacturer);
    } else {
      this.manager.SubmitManufacturer();
    }
  };

  @action SetEditingCertificates = bool => {
    this.editingCertificates = bool;
    if (bool && this.certificates) {
      this.certificates.forEach(cert => {
        if (cert.file) {
          this.selectableCertificates[cert.name].fileId = cert.file.id;
        }
      });
    }
  };

  @action SaveCertificates = () => {
    this.certificates = [];
    Object.keys(this.selectableCertificates).forEach(certificate => {
      if (this.selectableCertificates[certificate].fileId) {
        this.certificates.push({
          name: certificate,
          fileId: this.selectableCertificates[certificate].fileId,
        });
      }
    });
    this.manager.SubmitCertificates();
  };

  @action onUploadCertificateFile = (fileManager, certName) => {
    this.selectableCertificates[certName].fileId = fileManager.file.id;
  };

  @action RemoveCertificate = certName => {
    this.selectableCertificates[certName].fileId = '';
  };

  @action OnSetServicesError = error => {
    analytics.logError(error);
    Alert.error('Oops, there was and error when setting services data');
  };

  @action OnSetServicesSuccess = result => {
    if (this.upstream) {
      this.upstream.services = result.updateServices.map(service =>
        ServiceModel.Load(service),
      );
    }
    Alert.success('Services data has been successfully updated');
    routerStore.navigate(this.route.name, this.route.params);
  };

  @action SetServices = () => {
    if (!this.manager) return;
    const options = {
      onUserErr: this.OnSetServicesError,
      onSysErr: this.OnSetServicesError,
      onSuccess: this.OnSetServicesSuccess,
    };
    this.manager.SubmitManufacturerServices(this, options);
  };
}
AppRegistry.add(ManufacturerApp);

export class ManufacturerArrayApp extends ArrayApp {
  static registryName = 'ManufacturerArrayApp';
  static ElementApp = ManufacturerApp;
}
AppRegistry.add(ManufacturerArrayApp);
